import styled from 'styled-components';
import get from 'lodash/get';
import { SuiContainer } from '@splashthat/sui';
import withAppearTransition from 'common/hoc/withAppearTransition';
import { media } from 'common/styles/breakpoints';

import { bodyBoldFontWeight, latoFontFamilyStack, labelLightFontWeight } from 'common/styles/typography';
import { baseBorderRadius, containerPaddingM, buttonMediumPaddingM, containerPaddingMl } from 'common/styles/variables';
import {
    DEFAULT_TICKET_BACKGROUND_COLOR,
    DEFAULT_TICKET_BACKGROUND_OPACITY,
    DEFAULT_TICKET_BORDER_COLOR,
    DEFAULT_TICKET_BORDER_COLOR_HOVER,
    DEFAULT_TICKET_OVERLAY_COLOR,
    DEFAULT_TICKET_OVERLAY_OPACITY,
    DEFAULT_TICKET_FONT_SIZE,
    DEFAULT_SMALL_TICKET_FONT_SIZE,
    DEFAULT_TICKET_NAME_COLOR,
    DEFAULT_TICKET_DESCRIPTION_COLOR,
    DEFAULT_TICKET_REMAINING_COLOR,
    DEFAULT_TICKET_DATE_TIME_COLOR,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { TICKET_TYPE_NESTED } from 'common/entities/ticketType/selectors';

const SuiContainerWithAppearTransition = withAppearTransition(SuiContainer);

const PRICE_AREA = 'price';
const TITLE_AREA = 'title';
const DATES_AREA = 'dates';
const DESCRIPTION_AREA = 'description';
const QUANTITY_AREA = 'quantity';
const REMAINING_AREA = 'remaining';

export const TicketWrapperAppearTransition = styled(SuiContainerWithAppearTransition)`
    margin: ${containerPaddingM} 0;
    padding-left: ${({ category }) => (category === TICKET_TYPE_NESTED ? buttonMediumPaddingM : 0)};
    margin-bottom: ${containerPaddingMl};
`;

export const TicketOverlay = styled.div`
    background-color: ${({ backgroundColor }) => backgroundColor || DEFAULT_TICKET_OVERLAY_COLOR};
    opacity: ${({ opacity }) => (typeof opacity !== 'undefined' ? opacity : DEFAULT_TICKET_OVERLAY_OPACITY)};
    background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : '')};
    background-size: cover;
    background-position: center center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
`;

const gridByTicketStyle = {
    square: `
        min-height: 300px;
        align-content: center;
        grid-template-columns: 100%;
        grid-template-rows: auto;
        text-align: center;
        grid-template-areas:
            "${TITLE_AREA}"
            "${PRICE_AREA}"
            "${DATES_AREA}"
            "${DESCRIPTION_AREA}"
            "${QUANTITY_AREA}"
            "${REMAINING_AREA}";

        ${media.medium`;
            width: 300px;
        `}
    `,
    default: `
        grid-template-columns: 100%;
        grid-template-rows: auto;
        grid-template-areas:
            "${TITLE_AREA}"
            "${PRICE_AREA}"
            "${DESCRIPTION_AREA}"
            "${REMAINING_AREA}"
            "${DATES_AREA}"
            "${QUANTITY_AREA}";
    `,
};

const stubLeft = ({ ticketStyle }) => (ticketStyle !== 'square' ? 'justify-self: left;' : 'justify-self: center;');

const desktopStubRight = ({ ticketStyle }) => ticketStyle !== 'square' && 'justify-self: right;';

const desktopStubStyles = ({ ticketStyle }) =>
    ticketStyle !== 'square'
        ? `
        grid-template-columns: auto;
        grid-template-areas:
            "${TITLE_AREA} ${PRICE_AREA}"
            "${DESCRIPTION_AREA} ${DESCRIPTION_AREA}"
            "${REMAINING_AREA} empty"
            "${DATES_AREA} ${QUANTITY_AREA}";
    `
        : '';

export const TicketContainer = styled.div`
    margin: ${containerPaddingM} auto;
    ${({ category }) => (category === TICKET_TYPE_NESTED ? `margin-left: ${buttonMediumPaddingM};` : '')}

    border-color: ${({ borderColor }) => borderColor || DEFAULT_TICKET_BORDER_COLOR};
    border-width: ${({ borderWidths }) =>
        `${get(borderWidths, 'top', 1)}px ${get(borderWidths, 'right', 1)}px ${get(borderWidths, 'bottom', 1)}px ${get(
            borderWidths,
            'left',
            12,
        )}px`};
    border-radius: ${({ borderRadius }) => `${borderRadius}px` || baseBorderRadius};
    border-style: solid;
    padding: ${containerPaddingM};
    display: grid;
    justify-content: space-between;
    align-items: stretch;
    position: relative;
    z-index: 1;

    ${({ ticketStyle }) => gridByTicketStyle[ticketStyle] || gridByTicketStyle.default};

    ${media.medium`
            ${desktopStubStyles};
     `}

    &:hover {
        border-color: ${({ borderColorHover }) => borderColorHover || DEFAULT_TICKET_BORDER_COLOR_HOVER};
    }
`;

export const TicketQuantityContainer = styled.div`
    grid-area: ${QUANTITY_AREA};
    width: 100%;
    ${media.medium`
        width: auto;
        min-width: 8.75rem;
        max-width: 12.5rem;
        ${desktopStubRight}
    `}
    ${stubLeft}
    align-self: center;
    ${({ ticketStyle }) => ticketStyle === 'square' && 'margin-bottom: 1rem;'}
`;

export const TicketPriceContainer = styled.div`
    grid-area: ${PRICE_AREA};
    margin-bottom: ${containerPaddingM};
    ${stubLeft}
    ${media.medium`
        ${desktopStubRight}
    `}
`;

export const TicketTitle = styled.h1`
    word-break: break-word;
    grid-area: ${TITLE_AREA};
    margin-bottom: ${containerPaddingM};
    font-weight: ${bodyBoldFontWeight};
    color: ${({ color }) => color || DEFAULT_TICKET_NAME_COLOR};
    font-family: ${({ fontFamily }) => fontFamily || latoFontFamilyStack};
`;

export const TicketDateAndTime = styled.div`
    grid-area: ${DATES_AREA};
    line-height: normal;
    font-weight: ${labelLightFontWeight};
    color: ${({ color }) => color || DEFAULT_TICKET_DATE_TIME_COLOR};
    font-size: ${({ fontSize }) => (fontSize && `${fontSize}px`) || `${DEFAULT_SMALL_TICKET_FONT_SIZE}px`};
    font-family: ${({ fontFamily }) => fontFamily || latoFontFamilyStack};

    p {
        margin-bottom: 0.5rem;
    }
`;

export const TicketDescription = styled.p`
    word-break: break-word;
    grid-area: ${DESCRIPTION_AREA};
    color: ${({ color }) => color || DEFAULT_TICKET_DESCRIPTION_COLOR};
    font-size: ${({ fontSize }) => (fontSize && `${fontSize}px`) || `${DEFAULT_TICKET_FONT_SIZE}px`};
    font-family: ${({ fontFamily }) => fontFamily || latoFontFamilyStack};
    ${({ ticketStyle }) =>
        ticketStyle !== 'square'
            ? 'margin: 0 0 1rem 0;'
            : `margin: ${containerPaddingMl} 0;
        line-height: 1.4rem;`}
`;

export const TicketRemaining = styled.p`
    grid-area: ${REMAINING_AREA};
    color: ${({ color }) => color || DEFAULT_TICKET_REMAINING_COLOR};
    font-size: ${({ fontSize }) => (fontSize && `${fontSize}px`) || `${DEFAULT_TICKET_FONT_SIZE}px`};
    font-family: ${({ fontFamily }) => fontFamily || latoFontFamilyStack};
    ${({ ticketStyle }) =>
        ticketStyle !== 'square'
            ? `font-weight: ${bodyBoldFontWeight};
        margin-bottom: 1rem;`
            : `font-weight: ${labelLightFontWeight};
        margin-bottom: 0.5rem;`}
`;

export const TicketBackgroundContainer = styled.div`
    opacity: ${({ opacity }) => (opacity || opacity === 0 ? opacity : DEFAULT_TICKET_BACKGROUND_OPACITY)};
    background-color: ${({ backgroundColor }) => backgroundColor || DEFAULT_TICKET_BACKGROUND_COLOR};
    background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : '')};
    background-size: cover;
    background-position: center center;
    border-radius: ${({ borderRadius }) => `${borderRadius}px` || baseBorderRadius};
    position: absolute;
    height: 100%;
    right: 0;
    top: 0;
    left: 0;
    z-index: -1;
`;
